/**
 * ClearSlate / Gufa design-system tokens.
 *
 * Ported 1:1 from the design handoff (`clrslate-gufa-design-system`,
 * `tokens/*.css`). One teal spine + cool-slate neutrals + a pastel semantic
 * status set; matt, light surfaces. This is the single source of branding
 * truth — palettes, themes, and component overrides all derive from here.
 */

/** Brand teal scale — the spine. */
export const teal = {
  50: '#f0fdfa',
  100: '#cdfaf0',
  200: '#9bf2e2',
  300: '#5fe3d0',
  400: '#2dccbb',
  500: '#14b8a6', // bright accent — active nav, focus glows
  600: '#0d9488', // primary action hover
  700: '#0f766e', // PRIMARY — buttons, links, brand
  800: '#115e59',
  900: '#134e4a',
  950: '#0a3b3a',
} as const;

/** Deep petrol — the icon/nav rail (sidebar) + workspace chips. */
export const rail = {
  base: '#0c4a52',
  hover: '#0f5b63',
  active: '#14b8a6',
  border: '#14565e',
  text: '#cfe8e7',
  textDim: '#7fb3b2',
} as const;

/** Cool-slate neutrals. */
export const slate = {
  50: '#f8fafc',
  100: '#f1f5f9',
  200: '#e6eaef',
  300: '#cdd5df',
  400: '#94a3b8',
  500: '#64748b',
  600: '#475569',
  700: '#334155',
  800: '#1e293b',
  900: '#0f172a',
} as const;

/** Surfaces, borders, focus ring (matt, light). */
export const surfaces = {
  canvas: '#f6f7f9', // app background
  surface: '#ffffff', // cards, panels
  surface2: '#f8fafc', // subtle inset / table header / hover
  surface3: '#f1f5f9', // deeper inset
  border: '#e6eaef', // default hairline
  borderStrong: '#cdd5df',
  ring: 'rgba(20,184,166,.35)', // focus ring
} as const;

/** Text colors. */
export const text = {
  primary: '#1e293b',
  strong: '#0f172a',
  muted: '#64748b',
  subtle: '#94a3b8',
  onBrand: '#ffffff',
} as const;

/** Pastel semantic status — soft `bg` fill, readable `fg`, hairline `bd`, solid `dot`. */
export const status = {
  success: { main: '#16a34a', fg: '#15803d', bg: '#dcfce7', bd: '#bbf7d0', dot: '#22c55e' },
  danger: { main: '#dc2626', fg: '#b91c1c', bg: '#fee2e2', bd: '#fecaca', dot: '#ef4444' },
  warning: { main: '#e08a1e', fg: '#b45309', bg: '#fef3c7', bd: '#fde68a', dot: '#f59e0b' },
  info: { main: '#2563eb', fg: '#1d4ed8', bg: '#dbeafe', bd: '#bfdbfe', dot: '#3b82f6' },
  purple: { main: '#7c3aed', fg: '#6d28d9', bg: '#ede9fe', bd: '#ddd6fe', dot: '#8b5cf6' },
  brandSoft: { fg: '#0f766e', bg: '#d6f3ee', bd: '#a7e3d8' },
} as const;

/** Radius scale (px). Buttons/inputs `md`, cards `xl`, large panels `xxl`, pills `full`. */
export const radius = {
  xs: 4,
  sm: 6,
  md: 8,
  lg: 10,
  xl: 12,
  xxl: 16,
  full: 999,
} as const;

/** Soft, low-contrast shadows (tuned for light surfaces). */
export const shadows = {
  xs: '0 1px 2px rgba(15,23,42,.04)',
  sm: '0 1px 2px rgba(15,23,42,.05), 0 1px 1px rgba(15,23,42,.03)',
  md: '0 4px 12px rgba(15,23,42,.06), 0 1px 3px rgba(15,23,42,.04)',
  lg: '0 12px 28px rgba(15,23,42,.10), 0 4px 8px rgba(15,23,42,.05)',
  rail: '2px 0 14px rgba(8,40,44,.18)',
} as const;

/**
 * Dark-mode shadows. The design system is light-only, so light's subtle
 * `rgba(15,23,42,…)` shadows disappear on a dark canvas — these use deeper
 * black with more spread so elevation stays legible.
 */
export const shadowsDark = {
  xs: '0 1px 2px rgba(0,0,0,.40)',
  sm: '0 2px 4px rgba(0,0,0,.45), 0 1px 2px rgba(0,0,0,.30)',
  md: '0 6px 16px rgba(0,0,0,.55), 0 2px 6px rgba(0,0,0,.40)',
  lg: '0 18px 36px rgba(0,0,0,.65), 0 6px 12px rgba(0,0,0,.45)',
  rail: '2px 0 16px rgba(0,0,0,.55)',
} as const;

/** Soft card/divider border per mode (a light hairline on dark looks too sharp). */
export const borderColor = {
  light: surfaces.border,
  dark: 'rgba(226,232,240,0.10)',
} as const;

/** Motion — one easing curve, three speeds. */
export const motion = {
  ease: 'cubic-bezier(.2,.7,.3,1)',
  fast: '120ms',
  base: '180ms',
  slow: '260ms',
} as const;

/**
 * Typography — Geist (sans) + Geist Mono. Family names match the self-hosted
 * `@fontsource/geist-sans` ("Geist Sans") / `@fontsource/geist-mono` packages.
 */
export const fonts = {
  sans: '"Geist Sans", ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif',
  mono: '"Geist Mono", ui-monospace, "SF Mono", "Menlo", monospace',
  htmlFontSize: 16,
} as const;

/** Letter-spacing — tight for headings, wide for all-caps eyebrows. */
export const tracking = {
  tight: '-0.02em',
  snug: '-0.01em',
  wide: '0.06em',
} as const;

/** Logo fill — a bright teal that reads on the deep-petrol nav rail. */
export const logoFill = teal[300];
